import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, FileTextIcon, PenToolIcon, SearchIcon, TargetIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ContentService() {
  const features = [{
    icon: PenToolIcon,
    title: 'Website Copywriting',
    description: 'Engaging, conversion-focused content for all your pages'
  }, {
    icon: FileTextIcon,
    title: 'Blog Posts',
    description: 'SEO-optimised articles that attract and engage readers'
  }, {
    icon: SearchIcon,
    title: 'SEO Content',
    description: 'Keyword-rich content that ranks well in search engines'
  }, {
    icon: UsersIcon,
    title: 'Social Media',
    description: 'Compelling posts that drive engagement and shares'
  }, {
    icon: TargetIcon,
    title: 'Product Descriptions',
    description: 'Persuasive copy that converts browsers into buyers'
  }, {
    icon: TrendingUpIcon,
    title: 'Email Campaigns',
    description: 'Newsletter and promotional content that drives action'
  }];
  const benefits = ['Save time by outsourcing content creation', 'Improve SEO rankings with quality content', 'Engage your audience with professional copy', 'Increase conversions with persuasive messaging', 'Maintain consistent brand voice across platforms', 'Focus on your business while we handle the writing'];
  const process = [{
    step: '01',
    title: 'Discovery Call',
    description: 'We learn about your business, target audience, brand voice, and content goals.'
  }, {
    step: '02',
    title: 'Research & Planning',
    description: 'We research your industry, competitors, and keywords to create a content strategy.'
  }, {
    step: '03',
    title: 'Content Creation',
    description: 'Our writers create high-quality, engaging content tailored to your needs and audience.'
  }, {
    step: '04',
    title: 'Review & Revisions',
    description: 'You review the content and request any changes. We refine until you are completely satisfied.'
  }, {
    step: '05',
    title: 'Delivery & Publishing',
    description: 'We deliver the final content and can help with publishing to your website or platforms.'
  }];
  const packages = [{
    name: 'Starter Content',
    price: '£250',
    features: ['Up to 5 pages of website copy', 'SEO keyword research', 'Basic brand voice guide', '2 rounds of revisions', '5-7 day turnaround']
  }, {
    name: 'Growth Content',
    price: '£500',
    features: ['Up to 10 pages of website copy', 'Advanced SEO optimisation', '4 blog posts (500-800 words)', 'Detailed brand voice guide', '3 rounds of revisions', '7-10 day turnaround'],
    popular: true
  }, {
    name: 'Enterprise Content',
    price: '£1,000+',
    features: ['Unlimited pages of website copy', 'Comprehensive content strategy', '8+ blog posts per month', 'Social media content', 'Email campaigns', 'Unlimited revisions', 'Priority turnaround']
  }];
  return <>
      <Helmet data-id="element-2024">
        <title data-id="element-2025">Content Writing | Bright Pixxel Studio</title>
        <meta name="description" content="SEO-friendly website copy, product descriptions and blog content that sells, written for UK small businesses. Give your site a voice — get a free quote." data-id="element-2026" />
        <link rel="canonical" href="https://bright-pixxel.com/services/content" data-id="element-2027" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2028" />
        <meta property="og:type" content="website" data-id="element-2029" />
        <meta property="og:title" content="Content Writing | Bright Pixxel Studio" data-id="element-2030" />
        <meta property="og:description" content="SEO-friendly website copy, product descriptions and blog content that sells, written for UK small businesses. Give your site a voice — get a free quote." data-id="element-2031" />
        <meta property="og:url" content="https://bright-pixxel.com/services/content" data-id="element-2032" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2033" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2034" />
        <meta property="og:image:width" content="1200" data-id="element-2035" />
        <meta property="og:image:height" content="630" data-id="element-2036" />
        <meta property="og:image:type" content="image/png" data-id="element-2037" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Content Writing" data-id="element-2038" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2039" />
        <meta name="twitter:title" content="Content Writing | Bright Pixxel Studio" data-id="element-2040" />
        <meta name="twitter:description" content="SEO-friendly website copy, product descriptions and blog content that sells, written for UK small businesses. Give your site a voice — get a free quote." data-id="element-2041" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2042" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Content Writing" data-id="element-2043" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2044">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2045">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2046">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2047">
              <PenToolIcon size={40} className="text-[#0DC3DC]" data-id="element-2048" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2049">
              Website Content Writing Services (UK)
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2050">
              Professional, engaging content that connects with your audience
              and drives results. From website copy to blog posts, we create
              content that works.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2051">
              <Link to="/contact" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2052">
                Get a Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2053">
                View Samples
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2054">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2055">
          <div className="max-w-6xl mx-auto" data-id="element-2056">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2057">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2058">
                Content Writing Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2059">
                Professional content for every aspect of your business
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2060">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2061">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2062">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2063" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2064">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2065">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2066">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2067">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2068">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2069">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2070">
                Why Professional Content Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2071">
                Quality content is the foundation of successful digital
                marketing. It helps you connect with your audience, build trust,
                and drive conversions.
              </p>
              <div className="space-y-4" data-id="element-2072">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2073">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2074">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2075" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2076">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2077">
              <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Professional content writer working on engaging blog posts and website copy - Bright Pixxel Studio content writing services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2078" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2079">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2080">
          <div className="max-w-4xl mx-auto" data-id="element-2081">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2082">
              Our Content Creation Process
            </motion.h2>
            <div className="space-y-8" data-id="element-2083">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-2084">
                  <div className="flex-shrink-0" data-id="element-2085">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2086">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-2087">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-2088">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2089">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-2090">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Packages Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2091">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2092">
          <div className="max-w-6xl mx-auto" data-id="element-2093">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2094">
              Content Writing Packages
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2095">
              {packages.map((pkg, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md ${pkg.popular ? 'ring-2 ring-[#0DC3DC]' : ''}`} data-id="element-2096">
                  {pkg.popular && <div className="text-center mb-4" data-id="element-2097">
                      <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-2098">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className="text-2xl font-bold mb-2" data-id="element-2099">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-[#0DC3DC] mb-6" data-id="element-2100">
                    {pkg.price}
                  </div>
                  <ul className="space-y-3" data-id="element-2101">
                    {pkg.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-2102">
                        <CheckIcon size={18} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-2103" />
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-2104">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className={`mt-8 block text-center px-6 py-3 rounded-lg font-medium transition-colors ${pkg.popular ? 'bg-[#0DC3DC] text-white hover:bg-[#0DC3DC]/90' : 'bg-white dark:bg-[#1E1E1E] border-2 border-[#0DC3DC] text-[#0DC3DC] hover:bg-[#0DC3DC] hover:text-white'}`} data-id="element-2105">
                    Get Started
                  </Link>
                </motion.div>)}
            </div>
            <div className="mt-8 text-center" data-id="element-2106">
              <p className="text-gray-600 dark:text-gray-300" data-id="element-2107">
                Need a custom package? We can create a tailored solution for
                your specific needs.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2108">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2109">
          <div className="max-w-5xl mx-auto" data-id="element-2110">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2111">
              Content That Delivers Results
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2112">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-center" data-id="element-2113">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2114">
                  +150%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2115">
                  Average increase in organic traffic
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-center" data-id="element-2116">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2117">
                  +85%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2118">
                  Improvement in engagement rates
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-center" data-id="element-2119">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2120">
                  +120%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2121">
                  Increase in conversion rates
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2122">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2123">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2124">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2125">
              Ready to Elevate Your Content?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2126">
              Get in touch today to discuss your content needs. We'll create
              engaging, SEO-optimised content that resonates with your audience
              and drives results.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2127">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-[#0C1B26] px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg" data-id="element-2128">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}